import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import {
  startLoadingFlowAssignmentPolicies,
  startReorderFlowAssignmentPolicyRules,
  startUpdateFlowAssignmentPolicyRule
} from "../../app/actions/flowAssignmentPolicyActions";
import { startLoadingCatalogModules } from "../../app/actions/catalogModuleActions";
import { getCatalogModuleOptionLabel, getModuleDisplayLabelById } from "../../app/helpers/flowEngineConfigHelper";
import { normalizeSpecialistRoleKey } from "../../app/helpers/activeSpecialistRoleHelper";

const DIRECTION_OPTIONS = [
  { id: "", labelKey: "admin.flowAssignment.allDirections", fallback: "Todas las direcciones" },
  { id: "send", labelKey: "admin.flowAssignment.send", fallback: "Ida" },
  { id: "return", labelKey: "admin.flowAssignment.return", fallback: "Regreso" }
];

const RULE_LABELS = {
  "sequential-round-robin": "Asignación secuencial",
  workload: "Por carga",
  "assignment-pool": "Autoasignación (mostrador)",
  "manual-tree": "Árbol de asignación",
  predictive: "Predictiva"
};

const AdminFlowAssignmentPoliciesScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { policies, loading } = useSelector((state) => state.flowAssignmentPolicy);
  const { catalogModules = [] } = useSelector((state) => state.catalogModule);
  const [moduleId, setModuleId] = useState("");
  const [direction, setDirection] = useState("");

  useEffect(() => {
    dispatch(startLoadingCatalogModules());
  }, [dispatch]);

  useEffect(() => {
    const filters = {};
    if (moduleId) {
      filters.catalogmodule_id = moduleId;
    }
    if (direction) {
      filters.direction = direction;
    }
    dispatch(startLoadingFlowAssignmentPolicies(filters));
  }, [direction, dispatch, moduleId]);

  const reload = () => {
    const filters = {};
    if (moduleId) {
      filters.catalogmodule_id = moduleId;
    }
    if (direction) {
      filters.direction = direction;
    }
    dispatch(startLoadingFlowAssignmentPolicies(filters));
  };

  const grouped = useMemo(() => policies || [], [policies]);

  const handleToggle = async (rule) => {
    const updated = await dispatch(startUpdateFlowAssignmentPolicyRule(rule.id, { enabled: !rule.enabled }));
    if (updated) {
      reload();
    }
  };

  const handleMove = async (policy, ruleIndex, delta) => {
    const rules = [...(policy.FlowAssignmentPolicyRule_To_FlowAssignmentPolicy || [])]
      .sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0));
    const nextIndex = ruleIndex + delta;
    if (nextIndex < 0 || nextIndex >= rules.length) {
      return;
    }
    const swapped = [...rules];
    const [moved] = swapped.splice(ruleIndex, 1);
    swapped.splice(nextIndex, 0, moved);
    const ok = await dispatch(startReorderFlowAssignmentPolicyRules(policy.id, swapped.map((item) => item.id)));
    if (ok) {
      reload();
    }
  };

  return (
    <FlowEngineGlobalGate>
      <div className="container-fluid py-3">
        <h3 className="mb-2">{t("admin.flowAssignment.title", "Políticas de asignación")}</h3>
        <p className="text-muted">
          {t(
            "admin.flowAssignment.help",
            "La primera regla encendida gana. Encender o apagar y reordenar cambia si el caso va a persona o al mostrador, sin tocar los botones send_to_next / return_to_previous."
          )}
        </p>
        <div className="row g-2 align-items-end mb-4">
          <div className="col-md-4">
            <label className="form-label">{t("admin.flowAssignment.module", "Módulo")}</label>
            <select className="form-select" value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {catalogModules.map((mod) => (
                <option key={mod.id} value={mod.id}>{getCatalogModuleOptionLabel(mod, t)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">{t("admin.flowAssignment.direction", "Dirección")}</label>
            <select className="form-select" value={direction} onChange={(event) => setDirection(event.target.value)}>
              {DIRECTION_OPTIONS.map((item) => (
                <option key={item.id || "all"} value={item.id}>{t(item.labelKey, item.fallback)}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && <p className="text-muted">{t("general.loading", "Cargando...")}</p>}

        {grouped.map((policy) => {
          const rules = [...(policy.FlowAssignmentPolicyRule_To_FlowAssignmentPolicy || [])]
            .sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0));
          const roleLabel = (role) => {
            const key = normalizeSpecialistRoleKey({ name: role?.name || role });
            return key
              ? t(`admin.flowModule.diagram.role.${key}`, role?.name || role)
              : (role?.name || role);
          };
          const fromRole = roleLabel(policy.FromCatalogRole_To_FlowAssignmentPolicy || policy.from_catalogrole_id);
          const toRole = roleLabel(policy.ToCatalogRole_To_FlowAssignmentPolicy || policy.to_catalogrole_id);
          const moduleRow = policy.CatalogModule_To_FlowAssignmentPolicy;
          const moduleName = getCatalogModuleOptionLabel(moduleRow, t)
            || getModuleDisplayLabelById(policy.catalogmodule_id, t, moduleRow?.name || policy.catalogmodule_id);
          return (
            <div className="card mb-3" key={policy.id}>
              <div className="card-header d-flex flex-wrap justify-content-between gap-2">
                <strong>{moduleName}</strong>
                <span>
                  {fromRole} → {toRole} · {policy.direction === "return"
                    ? t("admin.flowAssignment.return", "Regreso")
                    : t("admin.flowAssignment.send", "Ida")}
                </span>
              </div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>{t("admin.flowAssignment.order", "Orden")}</th>
                      <th>{t("admin.flowAssignment.rule", "Regla")}</th>
                      <th>{t("admin.flowAssignment.enabled", "Encendida")}</th>
                      <th>{t("admin.flowAssignment.actions", "Acciones")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule, index) => (
                      <tr key={rule.id}>
                        <td>{rule.sort_order}</td>
                        <td>
                          {t(`flow.assignment.rule.${rule.rule_code}`, RULE_LABELS[rule.rule_code] || rule.rule_code)}
                          {index === 0 && rule.enabled !== false && (
                            <span className="badge bg-success ms-2">
                              {t("admin.flowAssignment.winner", "Gana")}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={rule.enabled !== false}
                              onChange={() => handleToggle(rule)}
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-1"
                            disabled={index === 0}
                            onClick={() => handleMove(policy, index, -1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={index === rules.length - 1}
                            onClick={() => handleMove(policy, index, 1)}
                          >
                            ↓
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {!loading && !grouped.length && (
          <p className="text-muted">{t("admin.flowAssignment.empty", "No hay políticas para el filtro.")}</p>
        )}
      </div>
    </FlowEngineGlobalGate>
  );
};

export default AdminFlowAssignmentPoliciesScreen;
